import { useEffect, useMemo, useState } from 'react';
import { useAppSelector } from '../../../hook/useAppSelector';
import FallbackLoader from '../../ContentWrapper/Content/Loader/FallbackLoader';
import Table from '../../ContentWrapper/Common/Table/Table';
import { CASH_REGISTER_PAGE, COLUMN_MAP, SEARCH_INPUT_DEBOUNCE_DELAY } from '../../../constant/constants';
import wallet from '../../../assets/img/svg/table/wallet.svg';
import search_file from '../../../assets/img/svg/table/search-file.svg';
import { t } from 'i18next';
import {
  applySearchCashRegisterData,
  resetCashRegister,
  setSelectedCCY,
  setSelectedItem,
  setSelectedLocation,
} from '../../../store/slice/cash-register-slice';
import { useAppDispatch } from '../../../hook/useAppDispatch';
import CashRegisterDetails from './CashRegisterDetails';
import OperatorDetails from './OperatorDetails';
import { useFetchDetails } from '../../../hook/useFetchDetails';
import { setActivePageKey, setColumns } from '../../../store/slice/config/config';
import { setShowColumnOrder } from '../../../store/slice/global-filters';
import { resetRange } from '../../../store/slice/date-slice';
import { useRowDensity } from '../../../hook/useRowDensity';
import TableCellWithTooltip from '../../ContentWrapper/Common/Table/TableCellWithTooltip';
import { searchCashRegisterData } from '../../../interface/response/CashRegisterDataResponse';
import CashRegisterPayInPayOut from './CashRegisterPayInPayOut';

const CashRegisterPage = () => {
  const dispatch = useAppDispatch();

  const cashRegister = useAppSelector((state) => state.cashRegisterData);
  const cashRegisterData = useAppSelector((state) => state.cashRegisterData.data);
  const loading = useAppSelector((state) => state.cashRegisterData.loading);
  const selectedItem = useAppSelector((state) => state.cashRegisterData.selectedItem);
  const [showDetails, setShowDetails] = useState<boolean>(false);
  const [showPayInPayOut, setShowPayInPayOut] = useState<boolean>(false);
  const operatorModalOpen = useAppSelector((state) => state.operatorDetails.operatorModalOpen);
  const selectedOperator = useAppSelector((state) => state.operatorDetails.selectedOperator);
  const error = useAppSelector((state) => state.cashRegisterData.error);
  const searchInput = useAppSelector((state) => state.searchSlice.input);

  const columns = useMemo(() => {
    if (!cashRegister.filterData?.Collection?.length) return [];
    const cols = Object.keys(cashRegister.filterData.Collection[0]).map((col) => COLUMN_MAP[col] || col);
    cols.push(t('cashRegister.table.header.actions'));
    return cols;
  }, [cashRegister.filterData]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (searchInput !== null) {
        dispatch(applySearchCashRegisterData(searchInput));
      }
    }, SEARCH_INPUT_DEBOUNCE_DELAY);

    return () => clearTimeout(timeout);
  }, [dispatch, searchInput]);

  useEffect(() => {
    dispatch(setActivePageKey(CASH_REGISTER_PAGE));
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      setColumns({
        columns: columns,
        pageKey: CASH_REGISTER_PAGE,
        showDevice: true,
        showDeviceType: false,
        showPaymentType: false,
      }),
    );
  }, [dispatch, columns]);

  // --- Initial setup ---
  useEffect(() => {
    dispatch(resetCashRegister());
    dispatch(setShowColumnOrder(true));
  }, [dispatch]);

  useEffect(() => {
    dispatch(resetRange());
  }, [dispatch]);

  const { getCashRegisterDetails, getOperatorDetails } = useFetchDetails();
  const { rowDensityClass } = useRowDensity();

  const data = useMemo(() => {
    if (!cashRegister.filterData?.Collection?.length) return [];

    const filteredData = searchCashRegisterData(cashRegisterData, searchInput);

    return filteredData?.Collection.map((item) => ({
      ...Object.fromEntries(Object.entries(item).map(([key, value]) => [COLUMN_MAP[key] || key, value])),
      [t('cashRegister.table.header.actions')]: (
        <div className="images_container">
          <TableCellWithTooltip
            isImageColumn={true}
            icon={search_file}
            text={t('cashRegister.table.tooltip.details')}
            className={rowDensityClass}
            onClick={() => {
              setShowDetails(true);
              getCashRegisterDetails(item);
              dispatch(setSelectedLocation(item.LocationName));
              dispatch(setSelectedCCY(item.Currency));
              dispatch(setSelectedItem(item));
            }}
          />

          <TableCellWithTooltip
            isImageColumn={true}
            icon={wallet}
            text={t('cashRegister.table.tooltip.cashRegisterPayInPayOut')}
            className={rowDensityClass}
            onClick={() => {
              setShowPayInPayOut(true);
              dispatch(setSelectedLocation(item.LocationName));
              dispatch(setSelectedCCY(item.Currency));
              dispatch(setSelectedItem(item));
            }}
          />
        </div>
      ),
      ...Object.fromEntries(
        Object.entries(item).map(([key, value]) => {
          if (COLUMN_MAP[key] === t('cashRegister.table.header.device')) {
            return [COLUMN_MAP[key], value ? value : t('cashRegister.table.cellValue.perLocation')];
          }

          return [COLUMN_MAP[key], value];
        }),
      ),
    }));
  }, [cashRegister.filterData, dispatch, getCashRegisterDetails, rowDensityClass, cashRegisterData, searchInput]);

  return (
    <>
      {loading && !showDetails && <FallbackLoader />}

      <Table
        data={data}
        error={error ? error : ''}
        showFooter={false}
        className="cash_register_table"
        loaderIsShown={loading}
        dataLength={data.length}
      />

      {showDetails && selectedOperator && operatorModalOpen ? (
        <OperatorDetails getDetails={() => getOperatorDetails(selectedOperator)} setShowDetails={setShowDetails} />
      ) : (
        showDetails &&
        selectedItem &&
        !operatorModalOpen && (
          <CashRegisterDetails
            getDetails={() => getCashRegisterDetails(selectedItem)}
            setShowDetails={setShowDetails}
          />
        )
      )}
      {showPayInPayOut && (
        <CashRegisterPayInPayOut onClose={() => setShowPayInPayOut(false)} selectedItem={selectedItem} />
      )}
    </>
  );
};

export default CashRegisterPage;
